// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useEffect } from 'react'
import CasavioLogo from './shared/CasavioLogo'

const OWNER_ID = '26680bcf-f67b-490e-ba08-e1f25ddcfd01'

export function BetaCommunityGate({ supabase, session }) {
  const [checking, setChecking] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)

  useEffect(() => {
    const checkAccess = async () => {
      if (!session) { setChecking(false); return }
      const { data } = await supabase
        .from('beta_users').select('id').eq('email', session.user.email).maybeSingle()
      setHasAccess(!!data)
      setChecking(false)
    }
    checkAccess()
  }, [session])

  if (checking) return (
    <div style={{ minHeight: '100vh', background: '#F2EDE4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: 'Georgia', color: '#9A7B68' }}>Loading...</div>
    </div>
  )

  if (!session) return (
    <div style={{ minHeight: '100vh', background: '#F2EDE4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 24 }}>
      <div style={{ fontFamily: 'Georgia', fontSize: 20, color: '#2C1D14' }}>Beta Community</div>
      <div style={{ color: '#9A7B68', fontSize: 14 }}>Please sign in to access the beta community</div>
      <a href="/" style={{ color: '#B8721F' }}>Sign in to Casavio →</a>
    </div>
  )

  if (!hasAccess) return (
    <div style={{ minHeight: '100vh', background: '#F2EDE4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 48 }}>🏠</div>
      <div style={{ fontFamily: 'Georgia', fontSize: 20, color: '#2C1D14' }}>Beta access required</div>
      <div style={{ color: '#9A7B68', fontSize: 14, maxWidth: 300, lineHeight: 1.6 }}>
        The Casavio beta community is invitation only. Contact Kenneth to request access.
      </div>
      <a href="/" style={{ color: '#B8721F' }}>← Back to Casavio</a>
    </div>
  )

  return <BetaCommunity supabase={supabase} session={session} />
}

function MemberAvatar({ name, initials, isKenneth, engagement, onClick }) {
  const level = engagement?.level || 'none'
  const ringColor = level === 'high' ? '#B8721F' : level === 'medium' ? '#D4902A' : '#D4C5B5'
  const circumference = 2 * Math.PI * 25
  const dashFill = level === 'high' ? circumference : level === 'medium' ? circumference * 0.65 : level === 'low' ? circumference * 0.35 : 0

  return (
    <div
      onClick={onClick}
      style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: 72, cursor: 'pointer' }}
    >
      <div style={{ position: 'relative', width: 56, height: 56, marginBottom: 6 }}>
        <svg width={56} height={56} style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}>
          <circle cx={28} cy={28} r={25} fill="none" stroke="#E8E0D5" strokeWidth={2.5} />
          {dashFill > 0 && (
            <circle
              cx={28} cy={28} r={25}
              fill="none"
              stroke={ringColor}
              strokeWidth={2.5}
              strokeDasharray={`${dashFill.toFixed(1)} ${circumference.toFixed(1)}`}
              strokeLinecap="round"
            />
          )}
        </svg>
        <div style={{
          position: 'absolute', top: 6, left: 6,
          width: 44, height: 44, borderRadius: '50%',
          background: isKenneth ? '#B8721F' : '#9A7B68',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'Georgia', fontSize: 15, fontWeight: 600,
        }}>
          {initials}
        </div>
        {(engagement?.featuresTested > 0) && (
          <div style={{
            position: 'absolute', bottom: 0, right: 0,
            background: '#2C1D14', color: '#fff',
            borderRadius: '50%', width: 17, height: 17,
            fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1.5px solid #FDFAF6',
          }}>
            {engagement.featuresTested > 9 ? '9+' : engagement.featuresTested}
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#9A7B68', lineHeight: 1.3 }}>
        {(name || '').split(' ')[0]}
      </div>
      {isKenneth && (
        <div style={{ fontSize: 9, color: '#B8721F', marginTop: 2 }}>Founder</div>
      )}
    </div>
  )
}

export default function BetaCommunity({ supabase, session }) {
  const [notes, setNotes] = useState([])
  const [bugReports, setBugReports] = useState([])
  const [features, setFeatures] = useState([])
  const [betaUsers, setBetaUsers] = useState([])
  const [userProfile, setUserProfile] = useState(null)
  const [newNote, setNewNote] = useState('')
  const [posting, setPosting] = useState(false)
  const [showNoteForm, setShowNoteForm] = useState(false)
  const [activeTab, setActiveTab] = useState('notes')
  const [showFeaturesModal, setShowFeaturesModal] = useState(false)
  const [allFeatures, setAllFeatures] = useState([])
  const [featureFilter, setFeatureFilter] = useState('done')
  const [memberEngagement, setMemberEngagement] = useState({})
  const [selectedMember, setSelectedMember] = useState(null)

  const isKenneth = session?.user?.id === OWNER_ID

  useEffect(() => {
    loadData()
  }, [session?.user?.id])

  const loadData = async () => {
    const queries = [
      supabase
        .from('beta_notes')
        .select('*')
        .order('pinned', { ascending: false })
        .order('created_at', { ascending: false }),
      supabase
        .from('bug_reports')
        .select('*')
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(10),
      supabase
        .from('feature_requests')
        .select('*')
        .in('status', ['planned', 'in_progress'])
        .order('votes', { ascending: false })
        .limit(8),
      supabase
        .from('beta_users')
        .select('*')
        .order('created_at', { ascending: true }),
    ]

    if (session?.user?.id) {
      queries.push(
        supabase.from('profiles').select('first_name,last_name').eq('id', session.user.id).maybeSingle()
      )
    }

    const results = await Promise.all(queries)
    setNotes(results[0].data || [])
    setBugReports(results[1].data || [])
    setFeatures(results[2].data || [])
    setBetaUsers(results[3].data || [])
    if (results[4]) setUserProfile(results[4].data || null)

    const [featuresAllRes, bugsAllRes] = await Promise.all([
      supabase.from('feature_requests').select('*').order('created_at', { ascending: false }),
      supabase.from('bug_reports').select('reporter_name, created_at, status'),
    ])
    const allFeaturesData = featuresAllRes.data || []
    const allBugsData = bugsAllRes.data || []
    const notesData = results[0].data || []
    const betaUsersList = results[3].data || []
    setAllFeatures(allFeaturesData)

    // Build engagement for each beta member
    const engagement = {}
    betaUsersList.forEach(user => {
      const firstName = user.name?.split(' ')[0]
      const featuresTestedList = allFeaturesData.filter(f =>
        Array.isArray(f.tested_by) &&
        f.tested_by.some(t => t.name?.toLowerCase().includes(firstName?.toLowerCase()))
      )
      const notesPosted = notesData.filter(n =>
        n.author_name?.toLowerCase().includes(firstName?.toLowerCase())
      ).length
      const bugsReported = allBugsData.filter(b =>
        b.reporter_name?.toLowerCase().includes(firstName?.toLowerCase())
      ).length
      const score = (featuresTestedList.length * 2) + (notesPosted * 3) + (bugsReported * 4)
      engagement[user.name] = {
        featuresTested: featuresTestedList.length,
        featuresTestedList,
        notesPosted,
        bugsReported,
        score,
        level: score >= 20 ? 'high' : score >= 8 ? 'medium' : score > 0 ? 'low' : 'none',
      }
    })
    const doneFeatures = allFeaturesData.filter(f => f.status === 'done')
    engagement['Kenneth Krummes'] = {
      featuresTested: doneFeatures.length,
      featuresTestedList: doneFeatures,
      notesPosted: notesData.filter(n => n.author_name?.includes('Kenneth')).length,
      bugsReported: 0,
      score: 100,
      level: 'high',
    }
    setMemberEngagement(engagement)
  }

  const postNote = async () => {
    if (!newNote.trim() || !session) return
    setPosting(true)
    try {
      const authorName = [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || session.user.email
      const { error } = await supabase.from('beta_notes').insert({
        author_id: session.user.id,
        author_name: authorName,
        author_role: isKenneth ? 'Founder' : 'Beta Member',
        content: newNote.trim(),
        pinned: false,
      })
      if (!error) {
        setNewNote('')
        setShowNoteForm(false)
        loadData()
        if (!isKenneth) {
          await supabase.functions.invoke('notify-owner', {
            body: {
              event: 'beta_note_posted',
              data: {
                author: authorName,
                content: newNote.trim().substring(0, 100),
              },
            },
          }).catch(() => {})
        }
      }
    } finally {
      setPosting(false)
    }
  }

  const likeNote = async (note) => {
    if (!session) return
    const userId = session.user.id
    if ((note.liked_by || []).includes(userId)) return
    await supabase.from('beta_notes').update({
      likes: (note.likes || 0) + 1,
      liked_by: [...(note.liked_by || []), userId],
    }).eq('id', note.id)
    loadData()
  }

  const deleteNote = async (noteId) => {
    if (!isKenneth) return
    if (!window.confirm('Delete this note?')) return
    await supabase.from('beta_notes').delete().eq('id', noteId)
    loadData()
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    const diffMs = Date.now() - date
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffHours / 24)
    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const STATUS_CONFIG = {
    new: { label: 'New', color: '#DC2626', bg: '#FFF0F0' },
    reviewing: { label: 'Reviewing', color: '#B8721F', bg: '#FFF8E7' },
    resolved: { label: 'Fixed ✓', color: '#16A34A', bg: '#F0FDF4' },
  }

  const avatarInitials = (name) =>
    (name || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?'

  const myInitials = [userProfile?.first_name?.charAt(0), userProfile?.last_name?.charAt(0)]
    .filter(Boolean).join('').toUpperCase() || (session?.user?.email?.charAt(0).toUpperCase() || '?')

  return (
    <div style={{ minHeight: '100vh', background: '#F2EDE4' }}>

      {/* Header */}
      <div style={{ background: '#2C1D14', padding: '20px 24px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <CasavioLogo size={28} showText />
          <div style={{ marginTop: 12, fontFamily: 'Georgia', fontSize: 20, color: '#FDFAF6' }}>
            Beta Community
          </div>
          <div style={{ fontSize: 13, color: '#9A7B68', marginTop: 4 }}>
            {betaUsers.length > 0 ? `${betaUsers.length} founding members` : 'Founding members'} · Building the future of home management
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px' }}>

        {/* Founding members roster */}
        <div style={{ background: '#FDFAF6', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: '#9A7B68', fontWeight: 600, letterSpacing: 1, marginBottom: 14 }}>
            FOUNDING BETA MEMBERS
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <MemberAvatar
              name="Kenneth Krummes"
              initials="KK"
              isKenneth
              engagement={memberEngagement['Kenneth Krummes']}
              onClick={() => setSelectedMember({ name: 'Kenneth Krummes', isKenneth: true })}
            />

            {betaUsers.filter(u => u.email !== 'kennethkrummes@gmail.com').map(user => (
              <MemberAvatar
                key={user.id}
                name={user.name}
                initials={avatarInitials(user.name)}
                engagement={memberEngagement[user.name]}
                onClick={() => setSelectedMember({ name: user.name, isKenneth: false })}
              />
            ))}

            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: 72 }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: '#F2EDE4', color: '#9A7B68',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, border: '2px dashed #D4C5B5', marginBottom: 6,
              }}>+</div>
              <div style={{ fontSize: 11, color: '#9A7B68', lineHeight: 1.3 }}>Invite</div>
            </div>
          </div>

          {/* Engagement legend */}
          <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
            {[
              { color: '#B8721F', label: 'Very active' },
              { color: '#D4902A', label: 'Active' },
              { color: '#D4C5B5', label: 'Getting started' },
              { color: '#E8E0D5', label: 'Not yet active' },
            ].map(({ color, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: '#9A7B68', fontFamily: 'Calibri, sans-serif' }}>{label}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 20, marginTop: 16, paddingTop: 16, borderTop: '1px solid #F2EDE4' }}>
            <div>
              <div style={{ fontFamily: 'Georgia', fontSize: 16, color: '#2C1D14' }}>April 2026</div>
              <div style={{ fontSize: 11, color: '#9A7B68' }}>Beta started</div>
            </div>
            <div
              onClick={() => setShowFeaturesModal(true)}
              style={{ cursor: 'pointer' }}
            >
              <div style={{
                fontFamily: 'Georgia', fontSize: 22,
                color: '#B8721F',
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                textDecorationColor: '#D4C5B5',
              }}>
                {allFeatures.filter(f => f.status === 'done').length || '57'}
              </div>
              <div style={{ fontSize: 11, color: '#9A7B68' }}>Features shipped ↗</div>
            </div>
            <div>
              <div style={{ fontFamily: 'Georgia', fontSize: 16, color: '#2C1D14' }}>{bugReports.filter(b => b.status === 'resolved').length}</div>
              <div style={{ fontSize: 11, color: '#9A7B68' }}>Bugs fixed</div>
            </div>
          </div>
        </div>

        {/* Tab pills */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
          {[
            { id: 'notes', label: '💬 Notes', count: notes.length },
            { id: 'bugs', label: '🐛 Bugs', count: bugReports.length },
            { id: 'roadmap', label: '🗺️ Roadmap', count: null },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '8px 16px', borderRadius: 20, border: '1px solid',
                borderColor: activeTab === tab.id ? '#B8721F' : '#D4C5B5',
                background: activeTab === tab.id ? '#B8721F' : 'transparent',
                color: activeTab === tab.id ? '#fff' : '#9A7B68',
                cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
                flexShrink: 0, fontFamily: 'Calibri',
              }}
            >
              {tab.label}
              {tab.count > 0 && (
                <span style={{
                  marginLeft: 6,
                  background: activeTab === tab.id ? 'rgba(255,255,255,0.25)' : '#F2EDE4',
                  borderRadius: 10, padding: '0 6px', fontSize: 11,
                  color: activeTab === tab.id ? '#fff' : '#9A7B68',
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* NOTES TAB */}
        {activeTab === 'notes' && (
          <div>
            {session ? (
              <div style={{ marginBottom: 16 }}>
                {!showNoteForm ? (
                  <button
                    onClick={() => setShowNoteForm(true)}
                    style={{
                      width: '100%', background: '#FDFAF6',
                      border: '2px dashed #D4C5B5', borderRadius: 12,
                      padding: 16, cursor: 'pointer', color: '#9A7B68',
                      fontSize: 14, display: 'flex', alignItems: 'center', gap: 10,
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: '#B8721F', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'Georgia', fontSize: 12, flexShrink: 0,
                    }}>
                      {myInitials}
                    </div>
                    <span>Share a note with the beta team...</span>
                  </button>
                ) : (
                  <div style={{ background: '#FDFAF6', borderRadius: 12, padding: 16, border: '1px solid #D4C5B5' }}>
                    <textarea
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      placeholder="Share feedback, a bug you found, a feature idea, or just say hi..."
                      autoFocus
                      style={{
                        width: '100%', minHeight: 100, border: 'none',
                        background: 'transparent', fontFamily: 'Calibri',
                        fontSize: 15, color: '#2C1D14', resize: 'none',
                        outline: 'none', lineHeight: 1.6, boxSizing: 'border-box',
                      }}
                    />
                    <div style={{
                      display: 'flex', justifyContent: 'flex-end', gap: 8,
                      marginTop: 8, paddingTop: 8, borderTop: '1px solid #F2EDE4',
                    }}>
                      <button
                        onClick={() => { setShowNoteForm(false); setNewNote('') }}
                        style={{ background: 'none', border: 'none', color: '#9A7B68', cursor: 'pointer', fontSize: 14 }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={postNote}
                        disabled={posting || !newNote.trim()}
                        style={{
                          background: newNote.trim() ? '#B8721F' : '#D4C5B5',
                          color: '#fff', border: 'none', borderRadius: 8,
                          padding: '8px 20px', cursor: newNote.trim() ? 'pointer' : 'default',
                          fontFamily: 'Georgia', fontSize: 14,
                        }}
                      >
                        {posting ? 'Posting...' : 'Post Note'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                background: '#FFF8E7', borderRadius: 12, padding: 16,
                marginBottom: 16, border: '1px solid #B8721F', textAlign: 'center',
              }}>
                <div style={{ fontSize: 14, color: '#B8721F', marginBottom: 8 }}>Sign in to post notes</div>
                <a href="/" style={{ color: '#B8721F', fontSize: 13, textDecoration: 'underline' }}>
                  Sign in to Casavio →
                </a>
              </div>
            )}

            {notes.map(note => (
              <div
                key={note.id}
                style={{
                  background: '#FDFAF6', borderRadius: 12, padding: 20, marginBottom: 12,
                  border: note.pinned ? '1px solid #B8721F' : '1px solid #F2EDE4',
                }}
              >
                {note.pinned && (
                  <div style={{ fontSize: 11, color: '#B8721F', fontWeight: 600, marginBottom: 8 }}>
                    📌 Pinned by founder
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: note.author_role === 'Founder' ? '#B8721F' : '#9A7B68',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Georgia', fontSize: 13, fontWeight: 600, flexShrink: 0,
                  }}>
                    {avatarInitials(note.author_name)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: '#2C1D14', fontWeight: 600 }}>{note.author_name}</div>
                    <div style={{ fontSize: 11, color: '#9A7B68' }}>
                      {note.author_role} · {formatDate(note.created_at)}
                    </div>
                  </div>
                  {isKenneth && (
                    <button
                      onClick={() => deleteNote(note.id)}
                      style={{ background: 'none', border: 'none', color: '#D4C5B5', cursor: 'pointer', fontSize: 16 }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 15, color: '#2C1D14', lineHeight: 1.7, marginBottom: 12 }}>
                  {note.content}
                </div>
                <button
                  onClick={() => likeNote(note)}
                  disabled={!session || (note.liked_by || []).includes(session?.user?.id)}
                  style={{
                    background: 'none', border: '1px solid #F2EDE4', borderRadius: 20,
                    padding: '4px 12px', cursor: session && !(note.liked_by || []).includes(session?.user?.id) ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                    color: (note.liked_by || []).includes(session?.user?.id) ? '#B8721F' : '#9A7B68',
                  }}
                >
                  👍 {note.likes || 0}
                </button>
              </div>
            ))}

            {notes.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9A7B68', fontSize: 14 }}>
                No notes yet. Be the first to post!
              </div>
            )}
          </div>
        )}

        {/* BUGS TAB */}
        {activeTab === 'bugs' && (
          <div>
            <div style={{ fontSize: 13, color: '#9A7B68', marginBottom: 16, lineHeight: 1.6 }}>
              Bug reports from beta members. Your reports help make Casavio better for everyone.
            </div>
            {session && (
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  width: '100%', background: '#FDFAF6', border: '1px solid #D4C5B5',
                  borderRadius: 10, padding: 14, cursor: 'pointer', color: '#9A7B68',
                  fontSize: 14, marginBottom: 16, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                🐛 Report a Bug → (use Report a Bug in the app)
              </button>
            )}
            {bugReports.map(bug => {
              const status = STATUS_CONFIG[bug.status] || STATUS_CONFIG.new
              return (
                <div key={bug.id} style={{ background: '#FDFAF6', borderRadius: 12, padding: 16, marginBottom: 10, border: '1px solid #F2EDE4' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ fontSize: 14, color: '#2C1D14', fontWeight: 600, flex: 1, marginRight: 12 }}>
                      {bug.category}
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 600, color: status.color,
                      background: status.bg, padding: '3px 10px', borderRadius: 10, whiteSpace: 'nowrap',
                    }}>
                      {status.label}
                    </span>
                  </div>
                  {bug.description && (
                    <div style={{ fontSize: 13, color: '#9A7B68', lineHeight: 1.6, marginBottom: 8 }}>
                      {bug.description}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#9A7B68' }}>
                    {bug.reporter_name || 'Beta member'} · {formatDate(bug.created_at)}
                  </div>
                </div>
              )
            })}
            {bugReports.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9A7B68', fontSize: 14 }}>
                No bug reports yet. The app is working great! 🎉
              </div>
            )}
          </div>
        )}

        {/* ROADMAP TAB */}
        {activeTab === 'roadmap' && (
          <div>
            <div style={{ fontSize: 13, color: '#9A7B68', marginBottom: 20, lineHeight: 1.6 }}>
              What we're building next. Your votes shape the priority.
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: '#B8721F', fontWeight: 600, letterSpacing: 1, marginBottom: 12 }}>
                🔨 IN PROGRESS
              </div>
              {features.filter(f => f.status === 'in_progress').map(feature => (
                <div key={feature.id} style={{
                  background: '#FDFAF6', borderRadius: 10, padding: 16, marginBottom: 8,
                  border: '1px solid #F2EDE4', display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: '#2C1D14' }}>{feature.title}</div>
                    {feature.description && (
                      <div style={{ fontSize: 12, color: '#9A7B68', marginTop: 4, lineHeight: 1.5 }}>
                        {feature.description.substring(0, 80)}{feature.description.length > 80 ? '...' : ''}
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: 12, color: '#B8721F', background: '#FFF8E7',
                    borderRadius: 20, padding: '4px 10px', whiteSpace: 'nowrap', fontWeight: 600,
                  }}>
                    Building now
                  </div>
                </div>
              ))}
              {features.filter(f => f.status === 'in_progress').length === 0 && (
                <div style={{ fontSize: 13, color: '#9A7B68', padding: '8px 0' }}>Nothing in progress right now.</div>
              )}
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: '#9A7B68', fontWeight: 600, letterSpacing: 1, marginBottom: 12 }}>
                🗓️ COMING SOON
              </div>
              {features.filter(f => f.status === 'planned').map(feature => (
                <div key={feature.id} style={{
                  background: '#FDFAF6', borderRadius: 10, padding: 16, marginBottom: 8,
                  border: '1px solid #F2EDE4', display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: '#2C1D14' }}>{feature.title}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#9A7B68', fontSize: 12 }}>
                    ⭐ {feature.votes || 0}
                  </div>
                </div>
              ))}
              {features.filter(f => f.status === 'planned').length === 0 && (
                <div style={{ fontSize: 13, color: '#9A7B68', padding: '8px 0' }}>No planned features yet.</div>
              )}
            </div>

            {session && (
              <div style={{ background: '#2C1D14', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                <div style={{ fontFamily: 'Georgia', fontSize: 16, color: '#FDFAF6', marginBottom: 8 }}>
                  Shape what gets built next
                </div>
                <div style={{ fontSize: 13, color: '#9A7B68', marginBottom: 16 }}>
                  Vote on features in the app to influence our priorities
                </div>
                <button
                  onClick={() => window.location.href = '/'}
                  style={{
                    background: '#B8721F', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '10px 24px', cursor: 'pointer',
                    fontFamily: 'Georgia', fontSize: 14,
                  }}
                >
                  Open Casavio → Vote on Features
                </button>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 32, paddingTop: 20, borderTop: '1px solid #D4C5B5' }}>
          <div style={{ fontSize: 12, color: '#9A7B68', marginBottom: 8 }}>
            Thank you for helping build Casavio 🏠
          </div>
          <a href="/" style={{ color: '#B8721F', fontSize: 13 }}>← Back to the app</a>
        </div>
      </div>

      {/* Member Detail Card */}
      {selectedMember && (() => {
        const eng = memberEngagement[selectedMember.name] || {}
        const initials = selectedMember.isKenneth ? 'KK' : avatarInitials(selectedMember.name)
        return (
          <>
            <div
              onClick={() => setSelectedMember(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200 }}
            />
            <div style={{
              position: 'fixed',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '85%', maxWidth: 340,
              background: '#FDFAF6',
              borderRadius: 16,
              zIndex: 201,
              padding: 24,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: selectedMember.isKenneth ? '#B8721F' : '#9A7B68',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Georgia', fontSize: 16, fontWeight: 600, flexShrink: 0,
                }}>
                  {initials}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Georgia', fontSize: 16, color: '#2C1D14' }}>{selectedMember.name}</div>
                  <div style={{ fontSize: 12, color: '#9A7B68', marginTop: 2 }}>
                    {selectedMember.isKenneth ? 'Founder' : 'Beta Member'}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedMember(null)}
                  style={{ background: 'none', border: 'none', fontSize: 22, color: '#9A7B68', cursor: 'pointer', lineHeight: 1 }}
                >×</button>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                {[
                  { label: 'Features tested', value: eng.featuresTested || 0 },
                  { label: 'Notes posted', value: eng.notesPosted || 0 },
                  { label: 'Bugs reported', value: eng.bugsReported || 0 },
                ].map(({ label, value }) => (
                  <div key={label} style={{ flex: 1, textAlign: 'center', background: '#F2EDE4', borderRadius: 10, padding: '10px 4px' }}>
                    <div style={{ fontFamily: 'Georgia', fontSize: 18, color: '#2C1D14' }}>{value}</div>
                    <div style={{ fontSize: 10, color: '#9A7B68', marginTop: 2, lineHeight: 1.3, fontFamily: 'Calibri, sans-serif' }}>{label}</div>
                  </div>
                ))}
              </div>

              {eng.featuresTestedList?.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: '#9A7B68', letterSpacing: 1, marginBottom: 8, fontFamily: 'Calibri, sans-serif' }}>
                    FEATURES TESTED
                  </div>
                  <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {eng.featuresTestedList.slice(0, 25).map((f, i) => (
                      <div key={f.id || i} style={{
                        fontSize: 13, color: '#2C1D14', padding: '6px 0',
                        borderBottom: i < Math.min(eng.featuresTestedList.length, 25) - 1 ? '1px solid #F2EDE4' : 'none',
                        fontFamily: 'Calibri, sans-serif',
                      }}>
                        {f.title}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )
      })()}

      {/* Features Modal */}
      {showFeaturesModal && (
        <>
          <div
            onClick={() => setShowFeaturesModal(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 200,
            }}
          />
          <div style={{
            position: 'fixed',
            top: '5%', left: '50%',
            transform: 'translateX(-50%)',
            width: '90%', maxWidth: 560,
            maxHeight: '85vh',
            background: '#FDFAF6',
            borderRadius: 16,
            zIndex: 201,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #F2EDE4',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}>
              <div>
                <div style={{ fontFamily: 'Georgia', fontSize: 20, color: '#2C1D14' }}>
                  Features Shipped
                </div>
                <div style={{ fontSize: 13, color: '#9A7B68', marginTop: 2 }}>
                  {allFeatures.filter(f => f.status === 'done').length} shipped · {allFeatures.filter(f => f.status === 'in_progress').length} building · {allFeatures.filter(f => f.status === 'planned').length} planned
                </div>
              </div>
              <button
                onClick={() => setShowFeaturesModal(false)}
                style={{
                  background: 'none', border: 'none',
                  fontSize: 24, cursor: 'pointer', color: '#9A7B68',
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, padding: '12px 24px', borderBottom: '1px solid #F2EDE4', flexShrink: 0 }}>
              {[
                { id: 'done', label: '✓ Shipped' },
                { id: 'in_progress', label: '🔨 Building' },
                { id: 'planned', label: '📋 Planned' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setFeatureFilter(tab.id)}
                  style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 12,
                    border: '1px solid',
                    borderColor: featureFilter === tab.id ? '#B8721F' : '#D4C5B5',
                    background: featureFilter === tab.id ? '#B8721F' : 'transparent',
                    color: featureFilter === tab.id ? '#fff' : '#9A7B68',
                    cursor: 'pointer',
                  }}
                >
                  {tab.label}
                  <span style={{ marginLeft: 4, fontSize: 11 }}>
                    ({allFeatures.filter(f => f.status === tab.id).length})
                  </span>
                </button>
              ))}
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '0 24px' }}>
              {allFeatures.filter(f => f.status === featureFilter).map((feature, index) => {
                const filtered = allFeatures.filter(f => f.status === featureFilter)
                return (
                <div
                  key={feature.id}
                  style={{
                    padding: '14px 0',
                    borderBottom: index < filtered.length - 1 ? '1px solid #F2EDE4' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: '#2C1D14', marginBottom: 4, lineHeight: 1.4 }}>
                      {feature.title}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        padding: '2px 8px', borderRadius: 10,
                        color: feature.status === 'in_progress' ? '#B8721F'
                          : feature.status === 'done' ? '#16A34A'
                          : '#9A7B68',
                        background: feature.status === 'in_progress' ? '#FFF8E7'
                          : feature.status === 'done' ? '#F0FDF4'
                          : '#F2EDE4',
                      }}>
                        {feature.status === 'in_progress' ? '🔨 Building'
                          : feature.status === 'done' ? '✓ Shipped'
                          : feature.status === 'planned' ? '📋 Planned'
                          : feature.status}
                      </span>
                      {feature.votes > 0 && (
                        <span style={{ fontSize: 11, color: '#9A7B68' }}>
                          ⭐ {feature.votes}
                        </span>
                      )}
                    </div>
                  </div>

                  {feature.tested_by?.length > 0 && (
                    <div style={{ display: 'flex', flexShrink: 0 }}>
                      {feature.tested_by.map((tester, i) => (
                        <div
                          key={i}
                          title={`${tester.name} — ${tester.role}`}
                          style={{
                            width: 28, height: 28,
                            borderRadius: '50%',
                            background: tester.initials === 'KK' ? '#B8721F'
                              : tester.initials === 'TS' ? '#6B7280'
                              : tester.initials === 'HA' ? '#7C3AED'
                              : '#9A7B68',
                            color: '#fff',
                            display: 'flex', alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: 'Georgia', fontSize: 9,
                            fontWeight: 600,
                            border: '2px solid #FDFAF6',
                            marginLeft: i > 0 ? -8 : 0,
                            zIndex: feature.tested_by.length - i,
                            position: 'relative',
                            cursor: 'default',
                          }}
                        >
                          {tester.initials}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
              })}
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #F2EDE4',
              background: '#F2EDE4',
              flexShrink: 0,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 12, color: '#9A7B68' }}>
                Avatars show beta members who tested each feature
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
